
import { TenantBranding, TenantName } from '../types/tenant.types';
import { getApiEndpoint } from '../config/apiConfig';

const DEFAULT_BRANDING: TenantBranding = {
  primaryColor: '#0A3D62',
  secondaryColor: '#1E90FF',
  backgroundColor: '#F4F6F8',
  headingText: 'Geospatial Platform',
  subheadingText: 'Data Intelligence Dashboard',
  landingDescription: 'Advanced geospatial analysis and visualization platform'
};

export class BrandingLoader {
  private static brandingCache: Map<TenantName, TenantBranding> = new Map();

  /**
   * Load branding configuration for a specific tenant
   */
  static async loadBranding(projectName: TenantName): Promise<TenantBranding> {
    // Check cache first
    if (this.brandingCache.has(projectName)) {
      return this.brandingCache.get(projectName)!;
    }

    try {
      // Attempt to load theme.json from the backend
      const response = await fetch(
        getApiEndpoint(`branding/${projectName}/theme.json`)
      );

      if (response.ok) {
        const branding = await response.json();
        
        // Add logo and favicon URLs
        const fullBranding: TenantBranding = {
          ...branding,
          logoUrl: getApiEndpoint(`branding/${projectName}/logo.png`),
          faviconUrl: getApiEndpoint(`branding/${projectName}/favicon.ico`)
        };

        this.brandingCache.set(projectName, fullBranding);
        return fullBranding;
      }
    } catch (error) {
      console.warn(`Failed to load branding for ${projectName}:`, error);
    }

    // Fallback to default branding with project-specific logo paths
    const fallbackBranding: TenantBranding = {
      ...DEFAULT_BRANDING,
      logoUrl: getApiEndpoint(`branding/${projectName}/logo.png`),
      faviconUrl: getApiEndpoint(`branding/${projectName}/favicon.ico`)
    };

    this.brandingCache.set(projectName, fallbackBranding);
    return fallbackBranding;
  }

  /**
   * Apply branding to document (favicon, CSS variables, etc.)
   */
  static applyBranding(branding: TenantBranding): void {
    // Update CSS variables
    const root = document.documentElement;
    root.style.setProperty('--color-primary', branding.primaryColor);
    root.style.setProperty('--color-secondary', branding.secondaryColor);
    root.style.setProperty('--color-background', branding.backgroundColor);

    // Update favicon
    if (branding.faviconUrl) {
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.href = branding.faviconUrl;
    }

    // Update page title
    document.title = branding.headingText;
  }

  /**
   * Clear branding cache (useful for testing)
   */
  static clearCache(): void {
    this.brandingCache.clear();
  }
}